import {
  getBatchLockInformation,
  getLockedBatches,
  lockBatch,
  unlockBatch,
} from "../../services/locks/Locks";
import {
  getModelLockInformation,
  lockModel,
  unlockModel,
} from "../../services/locks/ModelLocks";
import { useLoginDetails } from "../login/UserDetailsProvider";
import {
  LockState,
  useBatchLockDispatch,
  useBatchLockState,
  useModelLockDispatch,
  useModelLockState,
} from "./contexts/GoodLockProvider";

export { ConfigAppLocks, IsLocked, Lock, Unlock, LockedBy };

type IsLocked = (
  modelUuid: string,
  bcId: string,
  batchUuid: string,
) => Promise<"user" | "other_batch" | "other_model" | "none">;
type Lock = (
  modelUuid: string,
  bcId: string,
  batchUuid: string,
) => Promise<boolean>;
type Unlock = (
  modelUuid: string,
  bcId: string,
  batchUuid: string,
) => Promise<boolean>;
/**
 * @returns Name of user that has lock
 */
type LockedBy = (modelUuid: string, batchUuid: string) => string | undefined;

type Props = {
  children: (
    isLocked: IsLocked,
    lock: Lock,
    unlock: Unlock,
    lockedBy: LockedBy,
    unlockAll: () => Promise<boolean>[],
  ) => React.ReactElement;
};

/**
 * Provides lock methods for <AppNew/>
 * @returns
 */
const ConfigAppLocks = ({ children }: Props) => {
  const userDetails = useLoginDetails();
  const batchLockState = useBatchLockState();
  const batchLockDispatch = useBatchLockDispatch();
  const modelLockState = useModelLockState();
  const modelLockDispatch = useModelLockDispatch();

  /**
   *
   * @param modelUuid if empty string, batch lock will be checked
   * @param bcId
   * @param batchUuid
   * @returns
   */
  const isLocked: IsLocked = async (modelUuid, bcId, batchUuid) => {
    let lockedBy: "user" | "other_batch" | "other_model" | "none";

    const modelLock = modelLockState[modelUuid];
    const batchLock = batchLockState[batchUuid];

    // check if batch is locked directly
    if (modelUuid === "" || modelUuid == undefined) {
      console.debug(`Model not exist, checking if Batch ${batchUuid} locked`);
      // determine if batchLocked already exists
      if (batchLock) {
        lockedBy =
          batchLock[0] === userDetails.username ? "user" : "other_batch";
      } else {
        // check API if not previously locked
        const batchCheckoutRecord = await getBatchLockInformation(
          batchUuid,
          bcId,
        );
        lockedBy = !batchCheckoutRecord
          ? "none"
          : batchCheckoutRecord.createdBy === userDetails.username
            ? "user"
            : "other_batch";
      }
      console.debug(`New Model, Batch Locked by ${lockedBy}`);
      return lockedBy;
    }

    // return quickly if batch or model already has lock information
    if (modelLock) {
      lockedBy = modelLock[0] === userDetails.username ? "user" : "other_model";
      console.debug(`Model Locked by ${lockedBy}`);
    } else if (batchLock) {
      lockedBy = batchLock[0] === userDetails.username ? "user" : "other_batch";
      console.debug(`Batch Locked by ${lockedBy}`);
    } else {
      // we know that the model exists, and no lock information has been stored. return model lock info
      const modelCheckout = await getModelLockInformation(modelUuid, bcId);
      lockedBy = !modelCheckout
        ? "none"
        : modelCheckout.createdBy === userDetails.username
          ? "user"
          : "other_model";
      console.debug(`Model Locked by ${lockedBy}`);
    }
    return lockedBy;
  };

  const unlock: Unlock = async (modelUuid, bcId, batchUuid) => {
    if (modelUuid === "") {
      const unlocked = await unlockBatch(batchUuid, bcId);
      if (unlocked) {
        const data: LockState = {};
        data[batchUuid] = ["", bcId, false];
        batchLockDispatch({
          data,
          type: "remove",
        });
      }
      console.info(`Unlocked Batch:${batchUuid} unlocked=${unlocked}`);
      return unlocked;
    }

    // trigger unlock, return result of unlock
    const unlocked = await unlockModel(modelUuid, bcId);
    if (unlocked) {
      // directly remove model lock from state
      const data: LockState = {};
      data[modelUuid] = ["", bcId, false];
      modelLockDispatch({
        data,
        type: "remove",
      });
      // update locked batches state after model unlock
      const lockedBatches = await getLockedBatches(bcId);
      if (lockedBatches) {
        const newLocks = lockedBatches.reduce(
          (prev, curr) => {
            prev[curr.checkedOutId] = [
              curr.createdBy,
              bcId,
              curr.parent == null || curr.parent == undefined,
            ];
            return prev;
          },
          {} as typeof batchLockState,
        );

        batchLockDispatch({
          data: newLocks,
          type: "set",
        });
      }
    }
    console.info(`Unlocked Model:${modelUuid} unlocked=${unlocked}`);
    return unlocked;
  };

  const lock: Lock = async (modelUuid, bcId, batchUuid) => {
    let locked: boolean;
    if (modelUuid === "") {
      locked = await lockBatch(batchUuid, bcId);
      if (locked) {
        const data: LockState = {};
        data[batchUuid] = [userDetails.username ?? "", bcId, true];
        batchLockDispatch({
          data,
          type: "add",
        });
      }
      console.info(`Locked Batch:${batchUuid} locked=${locked}`);
    } else {
      locked = await lockModel(modelUuid, bcId);
      if (locked) {
        const data: LockState = {};
        data[modelUuid] = [userDetails.username ?? "", bcId, true];
        modelLockDispatch({
          data,
          type: "add",
        });
      }
      console.info(`Locked Model:${modelUuid} locked=${locked}`);
    }

    return locked;
  };

  /**
   * Should only be called once isLocked indicatese the batch is locked already.
   * @param modelUuid
   * @param batchUuid
   * @returns
   */
  const lockedBy: LockedBy = (modelUuid, batchUuid) => {
    const modelLock = modelLockState[modelUuid];
    if (modelLock) return modelLock[0];
    const batchLock = batchLockState[batchUuid];
    if (batchLock) return batchLock[0];
  };

  const unlockAll = () => {
    const promises: Promise<boolean>[] = [];
    Object.entries(modelLockState).map((val) => {
      const modelUuid = val[0];
      const [lockedBy, bcId, root] = val[1];
      if (lockedBy === userDetails.username && root) {
        promises.push(unlockModel(modelUuid, bcId));
      }
    });
    Object.entries(batchLockState).map((val) => {
      const batchUuid = val[0];
      const [lockedBy, bcId, root] = val[1];
      if (lockedBy === userDetails.username && root) {
        promises.push(unlockBatch(batchUuid, bcId));
      }
    });
    return promises;
  };

  return children(isLocked, lock, unlock, lockedBy, unlockAll);
};
